import LifeCycleStageModelling from '@app/components/food-modelling/components/life-cycle-stage-modelling';
import { EmissionFactorsSchema } from '@app/components/food-modelling/schema';
import {
  emptyEmissionFactors,
  formatEmissionFactors,
  getEmissionFactors,
} from '@app/components/food-modelling/utils';
import { getReportsPathMap } from '@app/components/reports/utils';
import ModelledFood from '@app/purchased-goods/food-and-drink/components/modelled-food';
import { useIntercom } from '@app/services/intercom/IntercomContext';
import { StageEnum } from '@app/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePatchIngredient } from '@shared/api/hooks/pcf/ingredients';
import {
  IngredientDetail,
  IngredientModelledAsEnum,
  ProductIngredientEntityTypeEnum,
} from '@shared/api/types';
import Heading from '@shared/components/content/heading';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import Unit from '@shared/components/content/unit';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import IngredientPicker from '@shared/components/pickers/ingredient-picker';
import { emptyBaseFoodRow } from '@shared/components/product-form/components/recipe-base-food-row';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import { formatSmallNumber } from '@shared/lib/utils';
import sentry from '@shared/services/sentry';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, Link } from 'react-router-dom';
import { useIngredientDetailContext } from '../ingredient-detail-context';
import { useIngredientModellingOptions } from '../ingredient-modelling-options';
import {
  ingredientDetailFormSchema,
  IngredientDetailFormSchema,
} from './ingredient-detail-form-schema';

export interface IngredientDetailFormProps {
  item: IngredientDetail;
}

export default function IngredientDetailForm({
  item,
}: IngredientDetailFormProps) {
  const { refetchItem } = useIngredientDetailContext();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showIntercomArticle } = useIntercom();

  const { mutateAsync: updateIngredient, isLoading: isUpdatingIngredient } =
    usePatchIngredient();

  const form = useForm<IngredientDetailFormSchema>({
    resolver: zodResolver(ingredientDetailFormSchema),
  });

  const { reset, handleSubmit, watch } = form;

  useEffect(() => {
    const stages: Array<StageEnum> = [...Object.keys(StageEnum)] as StageEnum[];

    const emissionFactors =
      item.modelledAs === IngredientModelledAsEnum.basic_food &&
      getEmissionFactors(
        stages,
        item.basicFood?.emissionFactors,
        item.overrideEmissionFactors!
      );

    if (item) {
      reset({
        name: item.name,
        modelledAs: item.modelledAs,
        basicFoodUuid: item.basicFood?.uuid || undefined,
        linkedItemUuid:
          item.modelledAs === IngredientModelledAsEnum.purchased_good
            ? item.purchasedGood?.uuid || ''
            : '',
        recipeDescription: item.name,
        emissionFactors:
          item.modelledAs === IngredientModelledAsEnum.basic_food
            ? (emissionFactors as EmissionFactorsSchema)
            : item.modelledAs === IngredientModelledAsEnum.modelled_food ||
                item.modelledAs === IngredientModelledAsEnum.purchased_good
              ? (formatEmissionFactors(
                  item.emissionsFactors!
                ) as EmissionFactorsSchema)
              : item.modelledAs ===
                  IngredientModelledAsEnum.external_emission_factor
                ? (item.overrideEmissionFactors as EmissionFactorsSchema)
                : undefined,
        ingredients:
          item.modelledAs === IngredientModelledAsEnum.modelled_food
            ? item.modelledFood?.ingredients.map((ingredient) => ({
                entityUuid: ingredient.baseFood.uuid,
                name: ingredient.baseFood.name,
                entityType: 'base_food' as ProductIngredientEntityTypeEnum,
                quantity: ingredient.quantity,
                unitUuid: ingredient.unit.uuid,
                availableUnitTypes: [],
              }))
            : ([emptyBaseFoodRow] as IngredientDetailFormSchema['ingredients']),
      });
    }
  }, [item, reset]);

  const onSubmit = async (data: IngredientDetailFormSchema) => {
    setIsSubmitting(true);

    const baseUpdateData = {
      customName: data.name,
      modelledAs: data.modelledAs,
    };
    const updateBasicFoodPayload = data.modelledAs ===
      IngredientModelledAsEnum.basic_food && {
      ...baseUpdateData,
      basicFoodUuid: data.basicFoodUuid,
      overrideEmissionFactors: Object.keys(data.emissionFactors!).reduce(
        (acc, stage) => {
          const factor = data.emissionFactors![stage as StageEnum];
          if (factor?.method === 'custom') {
            acc[stage as StageEnum] = {
              value: factor?.value,
              source: factor?.source || null,
            };
          }
          return acc;
        },
        emptyEmissionFactors as Record<
          StageEnum,
          { value: number | null; source: string | null }
        >
      ),
    };

    const updateLCAPayload = data.modelledAs ===
      IngredientModelledAsEnum.external_emission_factor && {
      ...baseUpdateData,
      customName: data.name,
      overrideEmissionFactors: Object.keys(data.emissionFactors!).reduce(
        (acc, stage) => {
          const factor = data.emissionFactors![stage as StageEnum];
          acc[stage as StageEnum] = {
            value: factor?.value,
            source: factor?.source || null,
          };
          return acc;
        },
        {} as Record<StageEnum, { value: number | null; source: string | null }>
      ),
    };

    const modelledFoodUpdate = data.modelledAs ===
      IngredientModelledAsEnum.modelled_food && {
      ...baseUpdateData,
      modelledFood: {
        ingredients: data.ingredients?.map((ingredient) => ({
          baseFoodUuid: ingredient.entityUuid,
          quantity: ingredient.quantity,
          unitUuid: ingredient.unitUuid,
        })),
      },
    };

    const purchasedGoodUpdate = data.modelledAs ===
      IngredientModelledAsEnum.purchased_good && {
      ...baseUpdateData,
      purchasedGoodUuid: data.linkedItemUuid,
    };

    const updateDataPayload =
      data.modelledAs === IngredientModelledAsEnum.basic_food
        ? updateBasicFoodPayload
        : data.modelledAs === IngredientModelledAsEnum.external_emission_factor
          ? updateLCAPayload
          : data.modelledAs === IngredientModelledAsEnum.purchased_good
            ? purchasedGoodUpdate
            : modelledFoodUpdate;

    try {
      await updateIngredient({
        ingredientUuid: item.uuid,
        data: updateDataPayload,
      });

      await refetchItem();
      toast({
        title: 'Item updated',
        variant: 'success',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Error updating item',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const modelledAs = watch('modelledAs');

  return (
    <Form {...form}>
      <form
        className="min-h-[360px] max-w-3xl space-y-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <fieldset disabled={isUpdatingIngredient}>
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-2">
              <InputFormField
                name="name"
                disabled
                label="Food name"
                className="col-span-6"
              />
            </div>
          </div>
          <div className="mt-8 space-y-4">
            <Heading variant="section">Modelling emissions</Heading>
            <div className="grid grid-cols-12 gap-2">
              <SelectFormField
                name="modelledAs"
                label="Modelled by"
                className="col-span-4"
                options={useIngredientModellingOptions()}
                showDescription
                showIcon
                includeErrorMessage
                handleSelect={(value) => {
                  if (
                    value ===
                      IngredientModelledAsEnum.external_emission_factor &&
                    item.modelledAs !==
                      IngredientModelledAsEnum.external_emission_factor
                  ) {
                    form.setValue('emissionFactors', emptyEmissionFactors);
                    return;
                  }

                  form.resetField('emissionFactors');
                }}
              />
              {modelledAs === IngredientModelledAsEnum.basic_food && (
                <IngredientPicker
                  name="basicFoodUuid"
                  include={['base_foods']}
                  label="Matched food"
                  className="col-span-5"
                  showLabel
                  knownSelectedItem={
                    item.basicFood
                      ? {
                          uuid: item.basicFood.uuid,
                          name: item.basicFood.name,
                          type: 'base_food',
                          availableUnitTypes: [],
                        }
                      : undefined
                  }
                />
              )}
              {modelledAs === IngredientModelledAsEnum.purchased_good && (
                <div className="col-span-5 flex items-end gap-2">
                  <IngredientPicker
                    name="linkedItemUuid"
                    include={['purchased_goods']}
                    label="Linked to"
                    className="col-span-5"
                    showLabel
                    knownSelectedItem={
                      item.purchasedGood
                        ? {
                            uuid: item.purchasedGood.uuid,
                            name: item.purchasedGood.name,
                            type: 'purchased_good',
                            availableUnitTypes: [],
                          }
                        : undefined
                    }
                  />

                  {item.modelledAs ===
                    IngredientModelledAsEnum.purchased_good &&
                    item.purchasedGood && (
                      <Link
                        to={generatePath(
                          getReportsPathMap().purchase.foodItem!,
                          {
                            item_uuid:
                              item.purchasedGood.purchasedGoodActivityUuid,
                          }
                        )}
                      >
                        <Icons.linkExternal className="mb-3 size-5 text-teal" />
                      </Link>
                    )}
                </div>
              )}
            </div>

            {item.modelledAs === IngredientModelledAsEnum.purchased_good &&
              modelledAs === IngredientModelledAsEnum.purchased_good && (
                <div className="space-y-3 pt-1">
                  <Heading variant="subsection">Emission factors</Heading>
                  <Text variant="subtle">
                    The emissions factors for this ingredient are determined by
                    the modelling of the purchased good it is linked to.
                  </Text>
                  <LifeCycleStageModelling
                    baseEmissionFactors={formatEmissionFactors(
                      item.emissionsFactors!
                    )}
                    showSourceColumn={false}
                  />
                </div>
              )}

            {modelledAs === IngredientModelledAsEnum.modelled_food && (
              <div className="mb-6 space-y-3 pt-1">
                <div className="flex items-center gap-4">
                  {item.modelledFood && (
                    <div className="flex flex-col items-center">
                      <RatingCloud rating={item.emissionsCategory} />
                      <Text>{formatSmallNumber(item.emissionFactor!)}</Text>
                      <Unit variant="kgC02e/kg" />
                    </div>
                  )}

                  {item.modelledFood ? (
                    <Text>
                      The emissions for this item are calculated based on the
                      carbon intensity of the recipe below. A carbon intensity
                      of {formatSmallNumber(item.emissionFactor!)} means that
                      for every 1 kg produced,{' '}
                      {formatSmallNumber(item.emissionFactor!)} kg of carbon
                      dioxide equivalent is emitted.
                    </Text>
                  ) : (
                    <Text>
                      The emissions for modelled foods are determined by their
                      ingredients. You can either input a recipe below or use
                      our tool to generate one from the description.
                    </Text>
                  )}
                </div>
                <ModelledFood
                  item={item}
                  modelledFood={item.modelledFood!}
                  isSubmitting={isSubmitting}
                />
              </div>
            )}

            {modelledAs ===
              IngredientModelledAsEnum.external_emission_factor && (
              <div className="space-y-3 pt-1">
                <Heading variant="subsection">Emission factors</Heading>
                <Text variant="subtle">
                  If you have completed a full product assessment with an
                  external provider or have the values from your supplier you
                  can enter them below. Please read our{' '}
                  <TextLink onClick={() => showIntercomArticle('customEFs')}>
                    guidelines for custom factors
                  </TextLink>{' '}
                  before doing so.
                </Text>
                <LifeCycleStageModelling
                  baseEmissionFactors={formatEmissionFactors(
                    item.emissionsFactors!
                  )}
                  allowCustomValues
                  showSourceColumn
                />
              </div>
            )}
            {modelledAs === IngredientModelledAsEnum.basic_food &&
              item.modelledAs === IngredientModelledAsEnum.basic_food && (
                <div className="space-y-3 pt-1">
                  <Heading variant="subsection">Emission factors</Heading>
                  <Text variant="subtle">
                    Basic foods have four emissions factors, covering the
                    life-cycle stages from the farm up to your supplier. As
                    standard, we select the most up-to-date factors available
                    for your location. You can override the factors with values
                    from your supplier or another source. Please read our{' '}
                    <TextLink onClick={() => showIntercomArticle('customEFs')}>
                      guidelines for custom factors
                    </TextLink>{' '}
                    before doing so.
                  </Text>
                  <LifeCycleStageModelling
                    baseEmissionFactors={item.basicFood?.emissionFactors}
                    showCustomSelector
                    allowCustomValues
                  />
                </div>
              )}
          </div>
          <div className="mt-12 space-y-4">
            <div className="col-span-4">
              <Button type="submit" loading={isUpdatingIngredient}>
                Update ingredient
              </Button>
            </div>
          </div>
        </fieldset>
      </form>
    </Form>
  );
}
